import { Router } from "express";
import { db } from "@workspace/db";
import { vendorsTable, vendorProductsTable, quoteRequestsTable, usersTable, vendorReviewsTable } from "@workspace/db";
import { eq, and, ilike, or, desc, inArray, sql, gte, lte, avg, count } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { sendMail, quoteReceivedHtml, quoteRespondedHtml } from "../lib/mailer";

const router = Router();

/* ── Public: distinct categories from products ─────────── */
router.get("/vendors/categories", async (_req, res, next) => {
  try {
    const rows = await db
      .selectDistinct({ category: vendorProductsTable.category })
      .from(vendorProductsTable)
      .where(eq(vendorProductsTable.isActive, true))
      .orderBy(vendorProductsTable.category);
    res.json(rows.map((r) => r.category).filter(Boolean));
  } catch (err) { next(err); }
});

/* ── Public: distinct cities from vendors ──────────────── */
router.get("/vendors/cities", async (_req, res, next) => {
  try {
    const rows = await db
      .selectDistinct({ city: vendorsTable.city })
      .from(vendorsTable)
      .where(eq(vendorsTable.isActive, true))
      .orderBy(vendorsTable.city);
    res.json(rows.map((r) => r.city).filter(Boolean));
  } catch (err) { next(err); }
});

/* ── Public: list/search vendors ───────────────────────── */
router.get("/vendors", async (req, res, next) => {
  try {
    const { city, category, search, verified, minRating, page = "1", limit = "20" } = req.query as Record<string, string>;
    const offset = (parseInt(page) - 1) * parseInt(limit);

    const conditions = [eq(vendorsTable.isActive, true)];
    if (city) conditions.push(ilike(vendorsTable.city, `%${city}%`));
    if (verified === "true") conditions.push(eq(vendorsTable.isVerified, true));
    if (minRating) conditions.push(gte(vendorsTable.rating, minRating));
    if (category) conditions.push(sql`${vendorsTable.categories} @> ${JSON.stringify([category])}::jsonb`);
    if (search) conditions.push(
      or(
        ilike(vendorsTable.companyName, `%${search}%`),
        ilike(vendorsTable.companyNameAr, `%${search}%`),
        ilike(vendorsTable.description, `%${search}%`),
        ilike(vendorsTable.sector, `%${search}%`),
      )!
    );

    const vendors = await db
      .select()
      .from(vendorsTable)
      .where(and(...conditions))
      .orderBy(desc(vendorsTable.isVerified), desc(vendorsTable.rating))
      .limit(parseInt(limit))
      .offset(offset);

    const [{ total }] = await db
      .select({ total: sql<number>`count(*)::int` })
      .from(vendorsTable)
      .where(and(...conditions));

    res.json({ vendors, total, page: parseInt(page), limit: parseInt(limit) });
  } catch (err) { next(err); }
});

/* ── Public: get vendor by id with products ────────────── */
router.get("/vendors/:id", async (req, res, next) => {
  try {
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.id, parseInt(req.params.id))).limit(1);
    if (!vendor) return res.status(404).json({ error: "Vendor not found" });

    const products = await db
      .select()
      .from(vendorProductsTable)
      .where(and(eq(vendorProductsTable.vendorId, vendor.id), eq(vendorProductsTable.isActive, true)))
      .orderBy(vendorProductsTable.category, vendorProductsTable.name);

    res.json({ ...vendor, products });
  } catch (err) { next(err); }
});

/* ── Public: search vendor products ────────────────────── */
router.get("/vendor-products/search", async (req, res, next) => {
  try {
    const { q, category, city, minPrice, maxPrice, page = "1", limit = "30" } = req.query as Record<string, string>;
    const offset = (parseInt(page) - 1) * parseInt(limit);

    const conditions = [eq(vendorProductsTable.isActive, true)];
    if (q) conditions.push(
      or(
        ilike(vendorProductsTable.name, `%${q}%`),
        ilike(vendorProductsTable.nameAr, `%${q}%`),
        ilike(vendorProductsTable.description, `%${q}%`),
        ilike(vendorProductsTable.category, `%${q}%`),
      )!
    );
    if (category) conditions.push(ilike(vendorProductsTable.category, `%${category}%`));
    if (minPrice) conditions.push(gte(vendorProductsTable.minPrice, minPrice));
    if (maxPrice) conditions.push(lte(vendorProductsTable.maxPrice, maxPrice));

    const products = await db
      .select({
        product: vendorProductsTable,
        vendor: {
          id: vendorsTable.id,
          companyName: vendorsTable.companyName,
          companyNameAr: vendorsTable.companyNameAr,
          city: vendorsTable.city,
          region: vendorsTable.region,
          phone: vendorsTable.phone,
          contactEmail: vendorsTable.contactEmail,
          isVerified: vendorsTable.isVerified,
          rating: vendorsTable.rating,
          logoUrl: vendorsTable.logoUrl,
        },
      })
      .from(vendorProductsTable)
      .innerJoin(vendorsTable, and(
        eq(vendorProductsTable.vendorId, vendorsTable.id),
        eq(vendorsTable.isActive, true),
        city ? ilike(vendorsTable.city, `%${city}%`) : sql`true`,
      ))
      .where(and(...conditions))
      .orderBy(desc(vendorsTable.isVerified), vendorProductsTable.name)
      .limit(parseInt(limit))
      .offset(offset);

    const [{ total }] = await db
      .select({ total: sql<number>`count(*)::int` })
      .from(vendorProductsTable)
      .innerJoin(vendorsTable, eq(vendorProductsTable.vendorId, vendorsTable.id))
      .where(and(...conditions));

    res.json({ products, total, page: parseInt(page), limit: parseInt(limit) });
  } catch (err) { next(err); }
});

/* ── Auth: get my vendor profile ────────────────────────── */
router.get("/my-vendor", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const products = await db
      .select()
      .from(vendorProductsTable)
      .where(eq(vendorProductsTable.vendorId, vendor.id))
      .orderBy(vendorProductsTable.category, vendorProductsTable.name);

    res.json({ ...vendor, products });
  } catch (err) { next(err); }
});

/* ── Auth: register as vendor ───────────────────────────── */
router.post("/my-vendor", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const existing = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (existing[0]) return res.status(409).json({ error: "Vendor profile already exists" });

    const {
      companyName, companyNameAr, sector, sectorAr, description, descriptionAr,
      city, region, address, contactEmail, phone, website, crNumber, vatNumber, categories, logoUrl,
    } = req.body;

    if (!companyName || !sector || !city || !contactEmail) {
      return res.status(400).json({ error: "companyName, sector, city and contactEmail are required" });
    }

    const [vendor] = await db.insert(vendorsTable).values({
      userId,
      companyName, companyNameAr, sector, sectorAr, description, descriptionAr,
      city, region, address, contactEmail, phone, website, crNumber, vatNumber,
      categories: categories ?? [],
      logoUrl: logoUrl ?? null,
      isActive: true,
    }).returning();

    res.status(201).json(vendor);
  } catch (err) { next(err); }
});

/* ── Auth: update my vendor profile ────────────────────── */
router.patch("/my-vendor", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const allowed = [
      "companyName","companyNameAr","sector","sectorAr","description","descriptionAr",
      "city","region","address","contactEmail","phone","website","logoUrl","crNumber","vatNumber","categories",
    ];
    const updates: Record<string, unknown> = {};
    for (const key of allowed) {
      if (req.body[key] !== undefined) updates[key] = req.body[key];
    }
    updates.updatedAt = new Date();

    const [updated] = await db.update(vendorsTable).set(updates).where(eq(vendorsTable.id, vendor.id)).returning();
    res.json(updated);
  } catch (err) { next(err); }
});

/* ── Auth: add product ──────────────────────────────────── */
router.post("/my-vendor/products", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const { name, nameAr, category, categoryAr, description, descriptionAr, unit, minPrice, maxPrice, inStock, minOrderQty, leadTimeDays, specs, tags, imageUrl } = req.body;
    if (!name || !category) return res.status(400).json({ error: "name and category are required" });

    const [product] = await db.insert(vendorProductsTable).values({
      vendorId: vendor.id,
      name, nameAr, category, categoryAr, description, descriptionAr,
      unit: unit ?? "piece",
      minPrice, maxPrice,
      inStock: inStock ?? true,
      minOrderQty: minOrderQty ?? 1,
      leadTimeDays,
      specs: specs ?? {},
      tags: tags ?? [],
      imageUrl: imageUrl ?? null,
      isActive: true,
    }).returning();

    res.status(201).json(product);
  } catch (err) { next(err); }
});

/* ── Auth: update product ───────────────────────────────── */
router.patch("/my-vendor/products/:productId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const [product] = await db.select().from(vendorProductsTable)
      .where(and(eq(vendorProductsTable.id, parseInt(req.params.productId as string)), eq(vendorProductsTable.vendorId, vendor.id)))
      .limit(1);
    if (!product) return res.status(404).json({ error: "Product not found" });

    const allowed = ["name","nameAr","category","categoryAr","description","descriptionAr","unit","minPrice","maxPrice","inStock","minOrderQty","leadTimeDays","specs","tags","isActive","imageUrl"];
    const updates: Record<string, unknown> = {};
    for (const key of allowed) {
      if (req.body[key] !== undefined) updates[key] = req.body[key];
    }
    updates.updatedAt = new Date();

    const [updated] = await db.update(vendorProductsTable).set(updates)
      .where(eq(vendorProductsTable.id, product.id)).returning();
    res.json(updated);
  } catch (err) { next(err); }
});

/* ── Auth: delete product ───────────────────────────────── */
router.delete("/my-vendor/products/:productId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    await db.delete(vendorProductsTable)
      .where(and(eq(vendorProductsTable.id, parseInt(req.params.productId as string)), eq(vendorProductsTable.vendorId, vendor.id)));
    res.json({ ok: true });
  } catch (err) { next(err); }
});

/* ── Auth: send quote request ───────────────────────────── */
router.post("/quote-requests", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const buyerUserId = req.dbUser!.id;
    const { tenderId, vendorId, subject, message, items } = req.body;
    if (!vendorId || !subject) return res.status(400).json({ error: "vendorId and subject are required" });

    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.id, parseInt(vendorId))).limit(1);
    if (!vendor) return res.status(404).json({ error: "Vendor not found" });

    const [quote] = await db.insert(quoteRequestsTable).values({
      tenderId: tenderId ? parseInt(tenderId) : null,
      buyerUserId,
      vendorId: parseInt(vendorId),
      subject,
      message,
      items: items ?? [],
      status: "pending",
    }).returning();

    res.status(201).json(quote);

    // Send email to vendor (fire-and-forget — response already sent)
    void (async () => {
      const buyer = req.dbUser!;
      const buyerName = [buyer.firstName, buyer.lastName].filter(Boolean).join(" ") || buyer.email;
      const [vendorUser] = await db
        .select({ email: usersTable.email, firstName: usersTable.firstName, language: usersTable.language })
        .from(usersTable)
        .where(eq(usersTable.id, vendor.userId))
        .limit(1);
      if (!vendorUser) return;
      const isAr = vendorUser.language === "ar";
      await sendMail({
        to: vendorUser.email,
        subject: isAr ? `طلب سعر جديد – ${subject}` : `New Quote Request – ${subject}`,
        html: quoteReceivedHtml(
          vendorUser.firstName ?? vendor.companyName,
          buyerName,
          subject,
          message ?? null,
          items ?? [],
          isAr,
        ),
      });
    })();
  } catch (err) { next(err); }
});

/* ── Auth: get my quote requests (buyer) ────────────────── */
router.get("/quote-requests/sent", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const quotes = await db
      .select({ quote: quoteRequestsTable, vendor: { id: vendorsTable.id, companyName: vendorsTable.companyName, city: vendorsTable.city, logoUrl: vendorsTable.logoUrl } })
      .from(quoteRequestsTable)
      .innerJoin(vendorsTable, eq(quoteRequestsTable.vendorId, vendorsTable.id))
      .where(eq(quoteRequestsTable.buyerUserId, userId))
      .orderBy(desc(quoteRequestsTable.createdAt));
    res.json(quotes);
  } catch (err) { next(err); }
});

/* ── Auth: get quote requests for vendor ────────────────── */
router.get("/quote-requests/received", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const quotes = await db
      .select({ quote: quoteRequestsTable, buyer: { id: usersTable.id, firstName: usersTable.firstName, lastName: usersTable.lastName, email: usersTable.email } })
      .from(quoteRequestsTable)
      .innerJoin(usersTable, eq(quoteRequestsTable.buyerUserId, usersTable.id))
      .where(eq(quoteRequestsTable.vendorId, vendor.id))
      .orderBy(desc(quoteRequestsTable.createdAt));
    res.json(quotes);
  } catch (err) { next(err); }
});

/* ── Auth: vendor responds to quote ─────────────────────── */
router.patch("/quote-requests/:id/respond", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const userId = req.dbUser!.id;
    const [vendor] = await db.select().from(vendorsTable).where(eq(vendorsTable.userId, userId)).limit(1);
    if (!vendor) return res.status(404).json({ error: "No vendor profile" });

    const { vendorResponse, quotedPrice, validUntil, status } = req.body;

    const [updated] = await db.update(quoteRequestsTable)
      .set({ vendorResponse, quotedPrice, validUntil: validUntil ? new Date(validUntil) : null, status: status ?? "responded", updatedAt: new Date() })
      .where(and(eq(quoteRequestsTable.id, parseInt(req.params.id as string)), eq(quoteRequestsTable.vendorId, vendor.id)))
      .returning();

    if (!updated) return res.status(404).json({ error: "Quote request not found" });
    res.json(updated);

    // Send email to buyer (fire-and-forget — response already sent)
    void (async () => {
      const [buyerUser] = await db
        .select({ email: usersTable.email, firstName: usersTable.firstName, language: usersTable.language })
        .from(usersTable)
        .where(eq(usersTable.id, updated.buyerUserId))
        .limit(1);
      if (!buyerUser) return;
      const isAr = buyerUser.language === "ar";
      const finalStatus = status ?? "responded";
      await sendMail({
        to: buyerUser.email,
        subject: isAr
          ? `ردّ على طلب السعر – ${updated.subject}`
          : `Quote Reply – ${updated.subject}`,
        html: quoteRespondedHtml(
          buyerUser.firstName ?? "",
          vendor.companyName,
          updated.subject,
          vendorResponse ?? null,
          quotedPrice ?? null,
          finalStatus,
          isAr,
        ),
      });
    })();
  } catch (err) { next(err); }
});

/* ── Public: get reviews for a vendor ───────────────────── */
router.get("/vendors/:id/reviews", async (req, res, next) => {
  try {
    const vendorId = parseInt(req.params.id as string);
    const reviews = await db
      .select({
        id: vendorReviewsTable.id,
        rating: vendorReviewsTable.rating,
        comment: vendorReviewsTable.comment,
        createdAt: vendorReviewsTable.createdAt,
        quoteRequestId: vendorReviewsTable.quoteRequestId,
        buyer: {
          firstName: usersTable.firstName,
          lastName: usersTable.lastName,
        },
      })
      .from(vendorReviewsTable)
      .innerJoin(usersTable, eq(vendorReviewsTable.buyerUserId, usersTable.id))
      .where(eq(vendorReviewsTable.vendorId, vendorId))
      .orderBy(desc(vendorReviewsTable.createdAt));

    res.json(reviews);
  } catch (err) { next(err); }
});

/* ── Auth: submit a review for a vendor ─────────────────── */
router.post("/vendors/:id/reviews", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const vendorId = parseInt(req.params.id as string);
    const buyerUserId = req.dbUser!.id;
    const { rating, comment, quoteRequestId } = req.body as { rating: number; comment?: string; quoteRequestId: number };

    if (!rating || rating < 1 || rating > 5) {
      return res.status(400).json({ error: "Rating must be between 1 and 5" });
    }
    if (!quoteRequestId) {
      return res.status(400).json({ error: "quoteRequestId is required" });
    }

    // Verify the quote belongs to this buyer and this vendor, and a response was exchanged
    const [quote] = await db
      .select()
      .from(quoteRequestsTable)
      .where(
        and(
          eq(quoteRequestsTable.id, quoteRequestId),
          eq(quoteRequestsTable.buyerUserId, buyerUserId),
          eq(quoteRequestsTable.vendorId, vendorId),
          inArray(quoteRequestsTable.status, ["responded", "accepted", "declined"]),
        ),
      )
      .limit(1);

    if (!quote) {
      return res.status(403).json({ error: "You can only review vendors after a quote has been exchanged" });
    }

    // Insert — unique constraint on quoteRequestId prevents duplicates
    const [review] = await db
      .insert(vendorReviewsTable)
      .values({ vendorId, buyerUserId, quoteRequestId, rating, comment: comment ?? null })
      .onConflictDoNothing()
      .returning();

    if (!review) {
      return res.status(409).json({ error: "You have already reviewed this quote" });
    }

    // Recalculate vendor average rating and total reviews
    const [stats] = await db
      .select({
        avgRating: avg(vendorReviewsTable.rating),
        total: count(vendorReviewsTable.id),
      })
      .from(vendorReviewsTable)
      .where(eq(vendorReviewsTable.vendorId, vendorId));

    await db
      .update(vendorsTable)
      .set({
        rating: stats.avgRating ?? "0",
        totalReviews: stats.total,
        updatedAt: new Date(),
      })
      .where(eq(vendorsTable.id, vendorId));

    res.status(201).json(review);
  } catch (err) { next(err); }
});

/* ── Auth: check if buyer can review a vendor ───────────── */
router.get("/vendors/:id/review-eligibility", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const vendorId = parseInt(req.params.id as string);
    const buyerUserId = req.dbUser!.id;

    // Quotes that are eligible for review (responded/accepted/declined)
    const eligibleQuotes = await db
      .select({ id: quoteRequestsTable.id, subject: quoteRequestsTable.subject })
      .from(quoteRequestsTable)
      .where(
        and(
          eq(quoteRequestsTable.buyerUserId, buyerUserId),
          eq(quoteRequestsTable.vendorId, vendorId),
          inArray(quoteRequestsTable.status, ["responded", "accepted", "declined"]),
        ),
      )
      .orderBy(desc(quoteRequestsTable.createdAt));

    // Which of those already have a review?
    const reviewedQuoteIds = eligibleQuotes.length > 0
      ? await db
          .select({ quoteRequestId: vendorReviewsTable.quoteRequestId })
          .from(vendorReviewsTable)
          .where(
            and(
              eq(vendorReviewsTable.vendorId, vendorId),
              eq(vendorReviewsTable.buyerUserId, buyerUserId),
            ),
          )
      : [];

    const reviewedSet = new Set(reviewedQuoteIds.map((r) => r.quoteRequestId));
    const unreviewedQuotes = eligibleQuotes.filter((q) => !reviewedSet.has(q.id));

    res.json({ eligibleQuotes, unreviewedQuotes });
  } catch (err) { next(err); }
});

export default router;
